import { motion } from "framer-motion";
import { useLang } from "@/i18n";
import { Check, X } from "lucide-react";

export default function Transparency() {
  const { t } = useLang();
  const others = [1, 2, 3, 4, 5, 6, 7].map((n) => t(`trans.o${n}`));
  const us = [1, 2, 3, 4, 5, 6, 7].map((n) => t(`trans.n${n}`));

  return (
    <section className="py-20 lg:py-28 bg-[#003087] relative overflow-hidden" data-testid="transparency-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 left-1/3 w-96 h-96 rounded-full bg-[#00AEEF]/15 blur-[110px]" />
        <div className="absolute -bottom-32 right-1/4 w-96 h-96 rounded-full bg-[#00AEEF]/15 blur-[110px]" />
      </div>
      <div className="relative max-w-5xl mx-auto px-5 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }} className="text-center">
          <p className="text-xs uppercase tracking-[0.22em] font-bold text-[#9BDDF7]">{t("trans.kicker")}</p>
          <h2 className="text-2xl sm:text-3xl lg:text-[2.6rem] font-extrabold tracking-tight text-white mt-4 leading-tight max-w-3xl mx-auto" data-testid="transparency-title">
            {t("trans.title")}
          </h2>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }}
            className="rounded-3xl bg-white/5 border border-white/10 p-8" data-testid="transparency-others"
          >
            <h3 className="text-lg font-bold text-[#F87171] uppercase tracking-wider">{t("trans.others")}</h3>
            <ul className="mt-6 space-y-4">
              {others.map((o, i) => (
                <li key={i} className="flex gap-3 text-sm text-[#C9D6EA]">
                  <span className="w-6 h-6 rounded-full bg-[#F87171]/15 flex items-center justify-center shrink-0">
                    <X size={13} className="text-[#F87171]" strokeWidth={3} />
                  </span>
                  {o}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: 0.12 }}
            className="rounded-3xl bg-white p-8 shadow-2xl" data-testid="transparency-us"
          >
            <h3 className="text-lg font-bold text-[#00AEEF] uppercase tracking-wider">{t("trans.us")}</h3>
            <ul className="mt-6 space-y-4">
              {us.map((u, i) => (
                <li key={i} className="flex gap-3 text-sm font-semibold text-[#003087]">
                  <span className="w-6 h-6 rounded-full bg-[#00AEEF]/12 flex items-center justify-center shrink-0">
                    <Check size={13} className="text-[#00AEEF]" strokeWidth={3} />
                  </span>
                  {u}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }} whileInView={{ opacity: 1, y: 0, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-8 rounded-3xl bg-[#00AEEF] px-8 py-8 text-center shadow-2xl" data-testid="transparency-box"
        >
          <p className="text-lg sm:text-2xl font-extrabold text-white leading-snug">{t("trans.box")}</p>
        </motion.div>
      </div>
    </section>
  );
}
